import { BASE_URL } from "../utils/constants";
import axiosClient from "./apiClient";

const leadsApi = {
  // Get all leads with optional filters
  getList: async (params = {}) => {
    try {
      const url = `${BASE_URL}/leads`;
      const response = await axiosClient.get(url, { params });
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get a single lead by ID
  getDetail: async (id) => {
    try {
      const url = `${BASE_URL}/leads/${id}`;
      const response = await axiosClient.get(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Create a new lead
  create: async (params) => {
    try {
      const url = `${BASE_URL}/leads`;
      const response = await axiosClient.post(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Update an existing lead
  update: async (id, params) => {
    try {
      const url = `${BASE_URL}/leads/${id}`;
      const response = await axiosClient.put(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Delete a lead
  delete: async (id) => {
    try {
      const url = `${BASE_URL}/leads/${id}`;
      const response = await axiosClient.delete(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Convert a lead to a user
  convert: async (id, params) => {
    try {
      const url = `${BASE_URL}/leads/${id}/convert`;
      const response = await axiosClient.post(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Communication history: manual touchpoints + system sends (campaign
  // emails, automation texts) merged into one timeline, newest first.
  getHistory: async (leadId) => {
    try {
      const url = `${BASE_URL}/leads/${leadId}/touchpoints`;
      const response = await axiosClient.get(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Record-page activity: lessons, class enrollments, attendance, and the
  // lead's nurture state. The communication feed stays on getHistory.
  // Card on file, two steps: a SetupIntent for Stripe Elements, then the
  // confirmed payment method to keep.
  setupCard: async (leadId) => {
    try {
      return await axiosClient.post(`${BASE_URL}/leads/${leadId}/setup_card`, {});
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // slot "backup" keeps it as their second card, beside the main one.
  confirmCard: async (leadId, paymentMethodId, slot = "main") => {
    try {
      return await axiosClient.post(`${BASE_URL}/leads/${leadId}/confirm_card`, { payment_method_id: paymentMethodId, slot });
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // The backup becomes the main card.
  swapCards: async (leadId) => {
    try {
      return await axiosClient.post(`${BASE_URL}/leads/${leadId}/swap_cards`, {});
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  removeCard: async (leadId, slot) => {
    try {
      return await axiosClient.post(`${BASE_URL}/leads/${leadId}/remove_card`, { slot });
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // Lessons and payments, one ledger per lesson category (StudentCard).
  getCard: async (leadId) => {
    try {
      return await axiosClient.get(`${BASE_URL}/leads/${leadId}/card`);
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  // The student's teachers, in order (the first is the main one).
  setTeachers: async (leadId, teacherIds) => {
    try {
      return await axiosClient.put(`${BASE_URL}/leads/${leadId}/teachers`, { teacher_ids: teacherIds });
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getActivity: async (leadId) => {
    try {
      const url = `${BASE_URL}/leads/${leadId}/activity`;
      const response = await axiosClient.get(url);
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Log a touchpoint (call/text/email/in_person). Optionally schedules the
  // next follow-up in the same request — the log-and-schedule-next loop.
  logTouchpoint: async (leadId, { channel, note, occurredAt, nextFollowUpOn, nextFollowUpNote } = {}) => {
    try {
      const url = `${BASE_URL}/leads/${leadId}/touchpoints`;
      const payload = { touchpoint: { channel, note, occurred_at: occurredAt } };
      if (nextFollowUpOn !== undefined) payload.next_follow_up_on = nextFollowUpOn;
      if (nextFollowUpNote !== undefined) payload.next_follow_up_note = nextFollowUpNote;
      const response = await axiosClient.post(url, payload);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteTouchpoint: async (leadId, touchpointId) => {
    try {
      const url = `${BASE_URL}/leads/${leadId}/touchpoints/${touchpointId}`;
      const response = await axiosClient.delete(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // --- The lead's file folder: photo + labeled documents ---

  getDocuments: async (leadId) => {
    try {
      const response = await axiosClient.get(`${BASE_URL}/leads/${leadId}/documents`);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  uploadDocument: async (leadId, { file, label }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (label) formData.append('label', label);
      const response = await axiosClient.post(`${BASE_URL}/leads/${leadId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteDocument: async (leadId, documentId) => {
    try {
      const response = await axiosClient.delete(`${BASE_URL}/leads/${leadId}/documents/${documentId}`);
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  uploadPhoto: async (leadId, file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const response = await axiosClient.post(`${BASE_URL}/leads/${leadId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  removePhoto: async (leadId) => {
    try {
      const response = await axiosClient.delete(`${BASE_URL}/leads/${leadId}/documents/photo`);
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Import leads from CSV
  importCSV: async (formData) => {
    try {
      const url = `${BASE_URL}/leads/import`;
      const response = await axiosClient.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

// Couples / partnerships — dancers who book together
export const partnershipsApi = {
  getList: async () => {
    try {
      const url = `${BASE_URL}/partnerships`;
      const response = await axiosClient.get(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  create: async ({ leadIds, name }) => {
    try {
      const url = `${BASE_URL}/partnerships`;
      const response = await axiosClient.post(url, { lead_ids: leadIds, name });
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  delete: async (id) => {
    try {
      const url = `${BASE_URL}/partnerships/${id}`;
      const response = await axiosClient.delete(url);
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

// Lead Statuses API
export const leadStatusesApi = {
  getList: async () => {
    try {
      const url = `${BASE_URL}/lead_statuses`;
      const response = await axiosClient.get(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  create: async (params) => {
    try {
      const url = `${BASE_URL}/lead_statuses`;
      const response = await axiosClient.post(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  update: async (id, params) => {
    try {
      const url = `${BASE_URL}/lead_statuses/${id}`;
      const response = await axiosClient.put(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  delete: async (id) => {
    try {
      const url = `${BASE_URL}/lead_statuses/${id}`;
      const response = await axiosClient.delete(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

// Custom Fields API
export const customFieldsApi = {
  getList: async () => {
    try {
      const url = `${BASE_URL}/custom_fields`;
      const response = await axiosClient.get(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  create: async (params) => {
    try {
      const url = `${BASE_URL}/custom_fields`;
      const response = await axiosClient.post(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  update: async (id, params) => {
    try {
      const url = `${BASE_URL}/custom_fields/${id}`;
      const response = await axiosClient.put(url, params);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  delete: async (id) => {
    try {
      const url = `${BASE_URL}/custom_fields/${id}`;
      const response = await axiosClient.delete(url);
      return response; // axiosClient interceptor already returns response.data
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default leadsApi;
